/**
 * `T-49` · «Mi cafetería» — [[ADR-029]].
 *
 * La sección del panel donde el dueño escribe lo suyo: el nombre del negocio,
 * la paleta, el lema de la portada, el identificador público, y la dirección y
 * el horario **de cada sucursal**.
 *
 * ── Por qué existe este archivo ───────────────────────────────────────────
 * La 046 añadió esas columnas y nadie podía tocarlas. `F3-Tasks` lo llamó «una
 * columna que miente»: la migración aplicada, ninguna cafetería con horario, y
 * la cabecera del menú público enseñando un hueco **sin que nada falle**. Que
 * no falle es lo que lo hace peor.
 *
 * ── Y en su propio módulo, no dentro de `menu.ts` ─────────────────────────
 * `ADR-029` lo dice: es la primera sección del panel que **no pertenece a un
 * módulo**, y se ve con menú o sin él. Meterla en el contrato del menú la ataría
 * a algo que una cafetería puede no tener contratado.
 */
import { z } from 'zod';
import { PALETAS } from './paleta.js';
/** `HH:MM`, que es como lo escribe y lo lee una persona. Sin segundos. */
const esquemaHora = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'La hora va como HH:MM, de 00:00 a 23:59.');
/**
 * Un turno de un día. **0 es domingo**, como `extract(dow from …)` y como la
 * columna `branch_hours.dia` de la 046 — que es de donde sale, y por eso no se
 * elige aquí otra convención.
 *
 * `cierra > abre` lo exige la base (`branch_hours_cierra_despues`) y también
 * este esquema: un turno que cruza la medianoche no se representa así, y
 * descubrirlo en un 500 sería descubrirlo tarde.
 */
export const esquemaTurno = z.object({
    dia: z.number().int().min(0).max(6),
    abre: esquemaHora,
    cierra: esquemaHora,
}).strict().refine((t) => t.cierra > t.abre, {
    message: 'La hora de cierre tiene que ser posterior a la de apertura.',
    path: ['cierra'],
});
/** Una sucursal, como la ve y la edita el dueño. */
export const esquemaSucursalDeMiCafeteria = z.object({
    id: z.string().uuid(),
    nombre: z.string().trim().min(2).max(60),
    direccion: z.string().trim().max(160).nullable(),
    telefono: z.string().trim().max(24).nullable(),
    /**
     * **Se enseña y no se toca** — `ADR-029` §4. Es un identificador IANA, no algo
     * que un cafetero reconozca, y se pone una vez porque un local no muda de
     * huso. Viaja porque hoy no la ve nadie, y un error de dedo en el alta no lo
     * descubre jamás nadie.
     */
    zona: z.string(),
    /**
     * `T-66` · Qué quiere el dueño que pase con ella, y si ya entró en servicio.
     *
     * `pending` es **creada y sin abrir todavía**, y no es lo mismo que
     * `suspended` —«pausada»— por mucho que las dos signifiquen «hoy no». La
     * pantalla las dice distinto porque lo que hay que hacer con cada una es
     * distinto: una espera a Lautinet, la otra espera al dueño.
     */
    estado: z.enum(['pending', 'active', 'suspended']),
    horario: z.array(esquemaTurno),
}).strict();
/** Todo lo que pinta la sección, de un viaje. */
/**
 * `T-71` · `ADR-015` — Los tres colores de una paleta propia.
 *
 * Tres y no más: los otros trece tokens del sistema —filetes, superficies,
 * texto secundario, el color del texto sobre el acento— los calcula
 * `derivarPaleta()` empujando cada uno hasta que se lee. Pedirle al dueño el
 * color de un filete sería pedirle que haga ese trabajo, y además cada color
 * fijado a mano deja de corregirse solo cuando cambia el fondo.
 */
export const esquemaColoresDeMarca = z.object({
    fondo: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    tinta: z.string().regex(/^#[0-9a-fA-F]{6}$/),
    acento: z.string().regex(/^#[0-9a-fA-F]{6}$/),
}).strict();
/** La décima clave: «los míos». Las nueve curadas siguen siendo el camino. */
export const PALETA_PROPIA = 'personalizada';
/**
 * Las diez claves que admite el motor, en el orden en que se enseñan.
 *
 * Se arma aquí y no en cada esquema para que las dos listas —la que valida lo
 * que llega y la que valida lo que sale— **no puedan separarse**. El día que se
 * añada una curada, entra en las dos a la vez o en ninguna.
 */
export const CLAVES_DE_PALETA = [PALETA_PROPIA, ...Object.keys(PALETAS)];
export const esquemaMiCafeteria = z.object({
    nombre: z.string(),
    paleta: z.enum(CLAVES_DE_PALETA),
    /**
     * Los colores propios, **o nulo**. Van juntos o no van: media paleta no es una
     * paleta, y el motor lo impide con un `check` que ata los tres a la clave.
     */
    colores: esquemaColoresDeMarca.nullable(),
    lema: z.string().nullable(),
    identificador: z.string(),
    sucursales: z.array(esquemaSucursalDeMiCafeteria),
}).strict();
/**
 * Lo que se cambia de la cafetería entera.
 *
 * Los tres opcionales y **`lema` además nullable**: ausente es «no lo toques» y
 * nulo es «bórralo». Son dos cosas distintas y la pantalla necesita las dos —
 * es el mismo criterio que `calorias` en el editor del menú.
 */
export const esquemaEditarCafeteria = z.object({
    nombre: z.string().trim().min(2).max(80).optional(),
    /**
     * Una de las nueve curadas, o `personalizada` — y entonces `colores` es
     * obligatorio. Lo comprueba el `refine` de abajo además del `check` del
     * motor: el mensaje en español llega antes y no gasta una transacción.
     */
    paleta: z.enum(CLAVES_DE_PALETA).optional(),
    colores: esquemaColoresDeMarca.nullable().optional(),
    lema: z.string().trim().min(2).max(120).nullable().optional(),
}).strict().refine((c) => c.paleta !== PALETA_PROPIA || (c.colores !== undefined && c.colores !== null), { message: 'Una paleta propia necesita sus tres colores.', path: ['colores'] }).refine(
//  Y al revés: mandar colores con una curada dejaría la fila en un estado que
//  el `check` del motor rechaza, con un 500 en vez de una frase.
(c) => c.colores === undefined || c.colores === null || c.paleta === PALETA_PROPIA, { message: 'Para usar tus colores, elige «Personalizar».', path: ['paleta'] });
/**
 * El identificador público nuevo.
 *
 * Ruta aparte y no un campo más de `esquemaEditarCafeteria`, y no es estilo:
 * renombrarlo **cambia la URL que el dueño reparte**, así que la pantalla tiene
 * que poder pedir confirmación de eso y sólo de eso. Mezclado con el nombre,
 * guardar un lema arrastraría una confirmación que nadie pidió.
 *
 * Mismas reglas que en el alta: minúsculas, números y guiones entre medias. Una
 * URL que no se puede dictar por teléfono es una URL que alguien teclea mal.
 */
export const esquemaRenombrarIdentificador = z.object({
    identificador: z.string().trim().min(3).max(40)
        .regex(/^[a-z0-9]+(-[a-z0-9]+)*$/, 'Sólo minúsculas, números y guiones entre medias.'),
}).strict();
/** Lo que se cambia de una sucursal. Ausente es «no lo toques». */
export const esquemaEditarSucursal = z.object({
    nombre: z.string().trim().min(2).max(60).optional(),
    direccion: z.string().trim().min(4).max(160).nullable().optional(),
    telefono: z.string().trim().min(8).max(24).nullable().optional(),
}).strict();
/**
 * El horario de una sucursal, **entero**.
 *
 * `PUT` y reemplazo completo, por lo mismo que las sucursales de un platillo:
 * la pantalla enseña la semana y guarda lo que quedó. Con altas y bajas por
 * turno, dos pestañas abiertas mezclarían dos semanas distintas.
 *
 * Lista vacía es válida y significa **«sin horario declarado»**, que es el
 * estado en que nacen todas y el que la cabecera del menú ya sabe pintar.
 */
export const esquemaHorarioDeSucursal = z.object({
    turnos: z.array(esquemaTurno).max(21),
}).strict();
/**
 * `T-49` · Lo que la consola manda para crear una sucursal — `ADR-029` §5.
 *
 * **La zona va aquí y no en el panel**, y es la decisión de §4: es un
 * identificador IANA, no algo que un cafetero reconozca, y un desplegable en el
 * panel invita a elegir «Mexico_City» porque suena más. La pone quien da de
 * alta, una vez, y en el panel se enseña sin dejar tocarla.
 */
export const esquemaCrearSucursal = z.object({
    nombre: z.string().trim().min(2).max(60),
    zona: z.string().trim().min(3).max(60),
}).strict();
/**
 * `T-66` · Crear una sucursal.
 *
 * Nada de estado: **nace pendiente y no se elige**. Quien la crea no decide si
 * está en servicio — eso es una factura, y la activa la consola. Lo deriva el
 * motor en la 082, igual que la etiqueta de una caja en la 068.
 */
export const esquemaNuevaSucursal = z.object({
    nombre: z.string().trim().min(2).max(60),
    /**
     * El huso. Se pone **una vez y aquí**: un local no muda de huso, y `ADR-029`
     * lo dejó como «se enseña y no se toca» justamente porque cambiarlo después
     * movería la hora de todos los sellos ya dados.
     */
    zona: z.string().trim().min(3).max(60),
}).strict();
/**
 * Pausar o reanudar una sucursal.
 *
 * `pending` no está: de ahí sólo se sale activándola, y activar es de la
 * consola. Un dueño no puede devolver una sucursal a «pendiente» — sería
 * deshacer una factura desde el panel.
 */
export const esquemaEstadoDeSucursal = z.object({
    estado: z.enum(['active', 'suspended']),
}).strict();
//# sourceMappingURL=mi-cafeteria.js.map